import { Box } from "@mantine/core";
import { AppRoute } from "app";
import { useAtomValue, useSetAtom } from "jotai";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import Flex from "components/Flex";
import { NoInternetIcon } from "components/Icons/NoInternet";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { additionalDocumentsAtom } from "atoms/additionalDocuments";
import { clientConfigAtom } from "atoms/clientConfig";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { vinPhotoAtom } from "atoms/vin/vin";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useEventTracking } from "hooks/useEventTracking";
import { usePageRouting } from "hooks/usePageRouting";
import { usePageTracking } from "hooks/usePageTracking";
import { Checkpoint } from "types/Checkpoint";

import { PhotosSection } from "./sections/PhotosSection";
import { PointsOfImpactSection } from "./sections/PointsOfImpactSection";
import { VehicleConditionSection } from "./sections/VehicleConditionSection";
import { useStyles } from "./styles";
import { serverSessionAtom } from "atoms/serverSession";

export const ClaimSummaryScreen = () => {
  const { classes } = useStyles();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);

  usePageTracking({ name: "Claim summary - view" });

  const { currentProgressStep, onTrackAndNext } = usePageRouting();
  const { trackEvent } = useEventTracking();
  const { t } = useClientTranslation();
  const serverSession = useAtomValue(serverSessionAtom);
  const hasVinInServerSession = !!serverSession?.vin;
  

  const toggleDrawer = useSetAtom(pageDrawerAtom);
  const damagePhotos = useAtomValue(damagePhotosAtom);
  const vehiclePhotos = useAtomValue(vehiclePhotosAtom);
  const vinPhoto = useAtomValue(vinPhotoAtom);
  const additionalDocuments = useAtomValue(additionalDocumentsAtom);
  const {
    additionalDocuments: additionalDocumentsConfig,
    vehicleCondition,
    pointsOfImpact,
    vin: vinConfig,
  } = useAtomValue(clientConfigAtom);

  const onContinue = () => {
    if (navigator?.onLine) {
      toggleDrawer(false);
      onTrackAndNext({ name: "Vehicle analysis" })();
    } else {
      toggleDrawer(true);
      trackEvent({ name: "Offline" });
    }
  };

  return (
    <PageLayout
      title={t("claim_summary.header")}
      dataTestId="claimSummary"
      currentStep={currentProgressStep}
      forceContinue={targetReached}
      bottomButtons={<Button onClick={onContinue}>{t("claim_summary.submit_claim_for_analysis")}</Button>}
    >
      <Flex mx={24} mb={32}>
        <Text sizeVariant="md" mb={32} mt={8} style={{ justifySelf: "start" }}>
          {t("claim_summary.instruction")}
        </Text>
        {!vehicleCondition.disabled && (
          <>
            <VehicleConditionSection />
            <Box my={24} className={classes.horizontalLine} />
          </>
        )}
        {!pointsOfImpact?.disabled && (
          <>
            <PointsOfImpactSection />
            <Box my={24} className={classes.horizontalLine} />
          </>
        )}
        <PhotosSection name={t("damage_photos.header")} editRoute={AppRoute.DamagePhotos} photos={damagePhotos} />
        <Box my={24} className={classes.horizontalLine} />
        <PhotosSection
          name={t("vehicle_corner_photos.header")}
          editRoute={AppRoute.VehiclePhotos}
          photos={vehiclePhotos}
        />
        {additionalDocumentsConfig && (
          <>
            <Box my={24} className={classes.horizontalLine} />
            <PhotosSection
              name={t("additional_documents.header")}
              editRoute={AppRoute.AdditionalDocuments}
              photos={additionalDocuments}
            />
          </>
        )}
        {vinConfig && !hasVinInServerSession && (
          <>
            <Box my={24} className={classes.horizontalLine} />
            <PhotosSection name={t("vin.header")} editRoute={AppRoute.Vin} photos={vinPhoto} />
          </>
        )}
      </Flex>
      <BottomDrawer
        heading={t("offline.header")}
        headingIcon={<NoInternetIcon />}
        callToActionOnClick={onContinue}
        callToActionText={t("offline.upload_photos")}
      >
        <Text sizeVariant="md">{t("offline.instruction")}</Text>
      </BottomDrawer>
    </PageLayout>
  );
};
